<template>
  <div class="box has-text-centered">
    <div class="is-size-3" v-katex:display="katex_formula"></div>
    <p>Die gegebenen Werte eingeben und die gesuchte Variable unterhalb auswählen.</p>

    <Component v-for="(variable, index) in variables" :key="index" :is="variable.component" :label="variable.label" :unit="variable.unit" :constant="variable.is_constant" v-model="variable.varValue" />

    <label class="label">Welche Variable ist gesucht?</label>
    <div class="field pb-2">
      <div class="dropdown is-300px-width" :class="{ 'is-active': dropdown_active }">
        <div class="dropdown-trigger is-300px-width">
          <button class="button is-300px-width" @click="toggleDropdown()">
            <span v-if="target_variable_label == null">Auswählen</span>
            <VariableLabelString v-else :label="target_variable_label" :has_desc="false" />
            <span class="icon is-small">
              <i class="fas fa-angle-down" aria-hidden="true"></i>
            </span>
          </button>
        </div>
        <div class="dropdown-menu is-300px-width">
          <div class="dropdown-content">
            <div class="dropdown-item is-clickable" v-for="(variable, index) in variables" :key="index" @click="selectTargetUnit(variable.label)" v-show="variable.label != target_variable_label && !variable.is_constant">
              <VariableLabelString :label="variable.label" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <p v-if="error_message != ''">
      {{ error_message }}
    </p>
    <p class="buttons is-centered">
      <button class="button is-primary" v-on:click="calculate">
        <span class="icon">
          <i class="fas fa-calculator"></i>
        </span>
        <span>Berechnen!</span>
      </button>
      <button class="button" v-on:click="reset">
        Zurücksetzen
      </button>
    </p>
    <p class="help is-danger" v-if="target_variable_error_active">Du musst noch die gesuchte Variable auswählen!</p>

    <hr />

    <h2 class="has-text-centered">Ergebnis</h2>
    <div class="has-overflow-x-auto">
      <table class="table is-hoverable has-text-left">
        <thead>
          <tr>
            <th>Größe</th>
            <th>Wert</th>
            <th>Einheit</th>
          </tr>
        </thead>
        <tbody class="is-size-7-mobile">
          <tr v-for="(variable, index) in result_variables" :key="index" v-bind:class="( JSON.stringify(target_variable_label) ==  JSON.stringify(variable.label) ) ? [ 'has-text-weight-bold', 'has-background-white-ter' ] : []">
            <td v-if="!variable.is_constant">
              <VariableLabelString :label="variable.label" />
            </td>
            <td v-else colspan=3>
              <VariableLabelString :label="variable.label" />
            </td>
            <td v-if="!variable.is_constant">
              {{ variable.varValue.unitValue }}
              <span v-if="!variable.unit.has_prefix" v-html="getUnitMultiplicatorNumber(variable)"></span>
            </td>
            <td v-if="!variable.is_constant">
              <VariableUnitString v-if="variable.unit.has_prefix" :unit="variable.unit" :prefix="getUnitMultiplicatorPrefix(variable)" />
              <VariableUnitString v-else :unit="variable.unit" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <h3>Ergebnis als URL teilen</h3>
    <div class="field has-addons has-addons-centered pb-5">
      <div class="control">
        <input class="input" type="text" placeholder="https://elektrotechnik-rechner.at/" v-model="result_url">
      </div>
      <div class="control">
        <a class="button is-primary" @click="copyResultURLToClipboard()">
          <span class="icon">
            <i class="fas fa-copy"></i>
          </span>
          <span>
            {{ copy_button }}
          </span>
        </a>
      </div>
    </div>
    <!--
    <img src="@/assets/images/advert/conrad_desktop.gif" class="is-clickable is-hidden-mobile" @click="trackClick('https://www.awin1.com/cread.php?s=2470174&v=11354&q=377133&r=951007')">
    <img src="@/assets/images/advert/conrad_mobile.gif" class="is-clickable is-hidden-tablet" @click="trackClick('https://www.awin1.com/cread.php?s=2470173&v=11354&q=377133&r=951007')">
    -->
  </div>
</template>

<script>
import DefaultUnitInput from '@/components/inputs/DefaultUnitInput.vue'
import VariableLabelString from '@/components/VariableLabelString.vue'
import VariableUnitString from '@/components/VariableUnitString.vue'
import { useRoute } from 'vue-router'

export default {
  name: 'DefaultCalculator',
  components: {
    DefaultUnitInput,
    VariableLabelString,
    VariableUnitString
  },
  props: {
    prop_id: Number,
    prop_target_calculator_index: Number,
    prop_katex_formula: String,
    prop_parser_formula: String,
    prop_variables: Array,
    prop_target_variable: Object
  },
  setup () {
    const route = useRoute()

    return {
      route
    }
  },
  methods: {
    reset () {
      this.$matomo && this.$matomo.trackEvent(this.route.path, 'reset')

      this.variables.forEach(function (variable) {
        variable.varValue.unitValue = 1
        variable.varValue.unitMultiplicator = 1
      })
    },
    trackClick (targeturl) {
      this.$matomo && this.$matomo.trackEvent(this.route.path, 'affiliate-conrad', '', 0)
      window.open(targeturl, '_blank').focus()
    },
    createResultURL () {
      const url = 'https://elektrotechnik-rechner.at' + this.route.path
      let params = '?calc=' + this.id
      for (const i in this.variables) {
        if (this.variables[i].label === this.target_variable_label) {
          params = params + '&target_var=' + this.variables[i].variable.toLowerCase()
        } else {
          params = params + '&' + this.variables[i].variable.toLowerCase() + '_val=' + this.variables[i].varValue.unitValue
          params = params + '&' + this.variables[i].variable.toLowerCase() + '_mul=' + Math.log10(this.variables[i].varValue.unitMultiplicator)
        }
      }
      this.result_url = url + params
    },
    copyResultURLToClipboard () {
      this.$matomo && this.$matomo.trackEvent(this.route.path, 'copied-link')

      // https://stackoverflow.com/questions/65129200/vue-copy-password-input-field-to-clipboard
      const textArea = document.createElement('textarea')

      //
      // *** This styling is an extra step which is likely not required. ***
      //
      // Why is it here? To ensure:
      // 1. the element is able to have focus and selection.
      // 2. if element was to flash render it has minimal visual impact.
      // 3. less flakyness with selection and copying which **might** occur if
      //    the textarea element is not visible.
      //
      // The likelihood is the element won't even render, not even a flash,
      // so some of these are just precautions. However in IE the element
      // is visible whilst the popup box asking the user for permission for
      // the web page to copy to the clipboard.
      //

      // Place in top-left corner of screen regardless of scroll position.
      textArea.style.position = 'fixed'
      textArea.style.top = '0'
      textArea.style.left = '0'

      // Ensure it has a small width and height. Setting to 1px / 1em
      // doesn't work as this gives a negative w/h on some browsers.
      textArea.style.width = '2em'
      textArea.style.height = '2em'

      // We don't need padding, reducing the size if it does flash render.
      textArea.style.padding = 0

      // Clean up any borders.
      textArea.style.border = 'none'
      textArea.style.outline = 'none'
      textArea.style.boxShadow = 'none'

      // Avoid flash of white box if rendered for any reason.
      textArea.style.background = 'transparent'

      textArea.value = this.result_url

      document.body.appendChild(textArea)

      textArea.select()

      try {
        const successful = document.execCommand('copy')
        const msg = successful ? 'successful' : 'unsuccessful'
        console.log('Copying text command was ' + msg)
        this.copy_button = 'Kopiert!'
      } catch (err) {
        console.log('Oops, unable to copy')
      }

      document.body.removeChild(textArea)
    },
    calculate () {
      console.log('calculating...')

      if (this.target_variable_label == null) {
        this.$matomo && this.$matomo.trackEvent(this.route.path, 'calculate-without-target')
        this.target_variable_error_active = true
      } else {
        this.$matomo && this.$matomo.trackEvent(this.route.path, 'calculate')

        this.target_variable_error_active = false

        this.copy_button = 'Kopieren'

        let targetVariable = null
        const usedVariables = {}

        for (const variable of this.variables) {
          if (variable.label === this.target_variable_label) {
            targetVariable = variable
          } else {
            usedVariables[variable.variable] = variable.varValue.unitValue * variable.varValue.unitMultiplicator
          }
        }

        const formula = this.nerdamer.solve(this.prop_parser_formula, targetVariable.variable)

        // DEBUG START
        /*
        console.log('prop_parser_formula')
        console.log(this.prop_parser_formula)
        console.log('targetVariable')
        console.log(targetVariable.variable)
        console.log('formula')
        console.log(formula.toString())
        */
        // DEBUG END

        let result = 0

        result = formula.evaluate(usedVariables).text('decimals')

        result = result.replace('[', '')
        result = result.replace(']', '')

        // DIRTY WORKAROUND FOR /rechner/grenzfrequenz-lc-glied START
        if (this.prop_parser_formula === 'f-(1/(2*pi*sqrt(L*C)))') {
          if (targetVariable.variable === 'C') {
            result = Math.pow(((1 / (this.prop_variables[0].varValue.unitValue * this.prop_variables[0].varValue.unitMultiplicator)) / (2 * Math.PI)), 2) / (this.prop_variables[2].varValue.unitValue * this.prop_variables[2].varValue.unitMultiplicator)
          }

          if (targetVariable.variable === 'L') {
            result = Math.pow(((1 / (this.prop_variables[0].varValue.unitValue * this.prop_variables[0].varValue.unitMultiplicator)) / (2 * Math.PI)), 2) / (this.prop_variables[3].varValue.unitValue * this.prop_variables[3].varValue.unitMultiplicator)
          }
        }
        // DIRTY WORKAROUND END

        // DEBUG START
        /*
        console.log('prop_variables')
        console.log(this.prop_variables)
        console.log('Result')
        console.log(result)
        */
        // DEBUG END

        for (const variable of this.variables) {
          if (variable.label === this.target_variable_label) {
            variable.varValue.unitValue = result
            variable.varValue = this.getOptimalMultiplicator(variable)
          }
        }

        this.result_variables = JSON.parse(JSON.stringify(this.variables))

        this.createResultURL()
      }
    },
    getOptimalMultiplicator (variable) {
      const optimizedValue = {
        unitValue: 0,
        unitMultiplicator: 1
      }

      if (variable.varValue.unitValue >= 1) {
        // Values above one
        if (variable.unit === 's') {
          switch (true) {
            case (((variable.varValue.unitValue / 60) < 60) && ((variable.varValue.unitValue / 60) >= 1)):
              optimizedValue.unitValue = variable.varValue.unitValue / 60
              optimizedValue.unitMultiplicator = 60
              break

            case (((variable.varValue.unitValue / 3600) < 24) && ((variable.varValue.unitValue / 3600) >= 1)):
              optimizedValue.unitValue = variable.varValue.unitValue / 3600
              optimizedValue.unitMultiplicator = 3600
              break

            case ((variable.varValue.unitValue / 86400) >= 1):
              optimizedValue.unitValue = variable.varValue.unitValue / 86400
              optimizedValue.unitMultiplicator = 86400
              break
            default:
              optimizedValue.unitValue = variable.varValue.unitValue
              optimizedValue.unitMultiplicator = 1
          }
        } else if (variable.unit === 'm') {
          switch (true) {
            /*
            // Hektometer und Dekameter
            case (((value / 10) < 100) && ((value / 10) > 1)):
              optimizedValue.unitValue = value / 10
              result['unit'] = 'da'+unit
              break

            case (((value / 100) < 1000) && ((value / 100) > 1)):
              optimizedValue.unitValue = value / 100
              result['unit'] = 'h'+unit
              break
            */

            case ((variable.varValue.unitValue / 1000) >= 1):
              optimizedValue.unitValue = variable.varValue.unitValue / 1000
              optimizedValue.unitMultiplicator = 1000
              break

            default:
              optimizedValue.unitValue = variable.varValue.unitValue
              optimizedValue.unitMultiplicator = 1
          }
        } else {
          switch (true) {
            case (((variable.varValue.unitValue / 1000) < 1000) && ((variable.varValue.unitValue / 1000) >= 1)):
              optimizedValue.unitValue = variable.varValue.unitValue / 1000
              optimizedValue.unitMultiplicator = 1000
              break

            case (((variable.varValue.unitValue / 1000000) < 1000) && ((variable.varValue.unitValue / 1000000) >= 1)):
              optimizedValue.unitValue = variable.varValue.unitValue / 1000000
              optimizedValue.unitMultiplicator = 1000000
              break

            case ((variable.varValue.unitValue / 1000000000) >= 1):
              optimizedValue.unitValue = variable.varValue.unitValue / 1000000000
              optimizedValue.unitMultiplicator = 1000000000
              break

            default:
              optimizedValue.unitValue = variable.varValue.unitValue
              optimizedValue.unitMultiplicator = 1
          }
        }
      } else if (variable.varValue.unitValue < 1) {
        // Values below one
        switch (true) {
          case (((variable.varValue.unitValue * 1000) < 1000) && ((variable.varValue.unitValue * 1000) >= 0.1)):
            optimizedValue.unitValue = variable.varValue.unitValue * 1000
            optimizedValue.unitMultiplicator = 1 / 1000
            break

          case (((variable.varValue.unitValue * 1000000) < 1000) && ((variable.varValue.unitValue * 1000000) >= 0.1)):
            optimizedValue.unitValue = variable.varValue.unitValue * 1000000
            optimizedValue.unitMultiplicator = 1 / 1000000
            break

          case (((variable.varValue.unitValue * 1000000000) < 1000) && ((variable.varValue.unitValue * 1000000000) >= 0.1)):
            optimizedValue.unitValue = variable.varValue.unitValue * 1000000000
            optimizedValue.unitMultiplicator = 1 / 1000000000
            break

          default:
            optimizedValue.unitValue = variable.varValue.unitValue
            optimizedValue.unitMultiplicator = 1
        }
      } else {
        console.error('Es ist ist Fehler bei der Einheiten-Konvertierung aufgetreten.')
      }
      return optimizedValue
    },
    toggleDropdown () {
      if (this.dropdown_active) {
        this.dropdown_active = false
      } else {
        this.dropdown_active = true
      }
    },
    selectTargetUnit (label) {
      this.target_variable_label = label
      this.dropdown_active = false
      this.target_variable_error_active = false
    },
    getUnitMultiplicatorPrefix (variable) {
      const multiplicator = parseFloat(variable.varValue.unitMultiplicator)
      switch (true) {
        case (multiplicator === 86400):
          return 'Tag/e'

        case (multiplicator === 3600):
          return 'Stunde/n'

        case (multiplicator === 60):
          return 'Minute/n'

        case (multiplicator === 1000000000):
          return 'G'

        case (multiplicator === 1000000):
          return 'M'

        case (multiplicator === 1000):
          return 'k'

        case (multiplicator === 0.001):
          return 'm'

        case (multiplicator === 0.000001):
          return '\\mu'

        case (multiplicator === 0.000000001):
          return 'n'

        default:
          return ''
      }
    },
    getUnitMultiplicatorNumber (variable) {
      const multiplicator = parseFloat(variable.varValue.unitMultiplicator)
      switch (true) {
        case (multiplicator === 1000000000):
          return ' * 10<sup>9</sup>'

        case (multiplicator === 1000000):
          return ' * 10<sup>6</sup>'

        case (multiplicator === 1000):
          return ' * 10<sup>3</sup>'

        case (multiplicator === 0.001):
          return ' * 10<sup>-3</sup>'

        case (multiplicator === 0.000001):
          return ' * 10<sup>-6</sup>'

        case (multiplicator === 0.000000001):
          return ' * 10<sup>-9</sup>'

        default:
          return ''
      }
    }
  },
  data () {
    return {
      id: this.prop_id,
      katex_formula: this.prop_katex_formula,
      variables: this.prop_variables,
      result_variables: JSON.parse(JSON.stringify(this.prop_variables)),
      error_message: '',
      dropdown_active: false,
      target_variable_label: this.prop_target_variable,
      target_variable_error_active: false,
      result_url: '',
      copy_button: 'Kopieren'
    }
  },
  created () {
    if (this.target_variable_label != null && this.id === (this.prop_target_calculator_index + 1)) {
      this.calculate()
    }
  }
}
</script>
